import type { TypeTheme } from "@revolt/state/stores/Theme";

import type { ShadowKey } from "../keys";
import type { ThemeDefinition } from "../types";

/**
 * Identifiers of the built-in presets
 */
export const PRESET_IDS = ["oceano", "recife", "abissal", "espuma"] as const;

export type PresetId = (typeof PRESET_IDS)[number];

/**
 * A built-in look the user can start from
 *
 * Presets only describe what differs from the base theme definition,
 * the user's own choices (colour, fonts, sizes) are applied on top.
 */
export interface PresetDefinition {
  id: PresetId;

  /**
   * Values copied into the user's settings when the preset is picked
   */
  defaults: {
    source: string;
    variant: TypeTheme["m3Variant"];
    mode?: TypeTheme["mode"];
  };

  /**
   * Colours used to draw the preview card in the settings
   */
  preview: {
    background: string;
    surface: string;
    primary: string;
    accent: string;
  };

  /**
   * Accent colour the tertiary roles are generated from
   */
  accent?: string;

  /**
   * Tint applied to neutral surfaces (0 keeps plain greys)
   */
  surfaceChroma?: number;

  /**
   * Extra letter spacing for all interface text
   */
  tracking: string;

  /**
   * Multiplier applied to the corner radius scale
   */
  radiusScale?: number;

  shadows?: Partial<Record<ShadowKey, string>>;
  gradients: ThemeDefinition["gradients"];
  components: ThemeDefinition["components"];
}

/**
 * Derive a preset from another one, overriding only what is given
 * @param base Preset to start from
 * @param overrides Values to replace (components are merged per component)
 * @returns New preset
 */
export function extendPreset(
  base: PresetDefinition,
  overrides: Partial<Omit<PresetDefinition, "components">> & {
    id: PresetId;
    components?: ThemeDefinition["components"];
  },
): PresetDefinition {
  const components = { ...base.components };
  for (const [name, values] of Object.entries(overrides.components ?? {})) {
    components[name] = { ...components[name], ...values };
  }

  return {
    ...base,
    ...overrides,
    gradients: { ...base.gradients, ...overrides.gradients },
    shadows: { ...base.shadows, ...overrides.shadows },
    components,
  };
}
